/**
 * Shared invoice-document URL resolution ladder.
 *
 * AB#20717 — Stripe PDF S3-serving. The backend now stores a 7-day signed S3 copy of
 * every Stripe invoice PDF (`invoice_pdf_s3_url`, PHP-built) alongside the existing
 * Stripe-hosted links. That S3 copy never expires mid-review the way a stale Stripe
 * link can (Bug 18510), so it is preferred first; the rest of the ladder is the
 * pre-existing Bug 18510 preference (hosted invoice page over the raw PDF link, since
 * some Stripe accounts' `invoice_pdf` link 404s while the hosted page always works).
 *
 * `override` is typically the caller's "freshly refreshed" cache entry (e.g.
 * `PriceBreakdownModal.vue`'s `refreshedInvoiceLinks`, populated by the Refresh button's
 * POST `order-item-invoices/{id}/refresh-link` response) — it always wins over the
 * row's own (possibly stale) persisted fields, field-by-field.
 *
 * Every field is optional/defensive: `invoice_pdf_s3_url` rolls out incrementally on
 * the backend, so rows from before that rollout (or any row simply missing the field)
 * fall through the ladder cleanly to the pre-existing Stripe links.
 */
export interface InvoiceDocumentUrlFields {
  invoice_pdf_s3_url?: string | null;
  hosted_invoice_url?: string | null;
  invoice_pdf?: string | null;
}

type MaybeInvoiceDocumentUrlFields =
  | InvoiceDocumentUrlFields
  | Record<string, unknown>
  | null
  | undefined;

/**
 * Resolve the best openable invoice document URL:
 *   `override.invoice_pdf_s3_url ?? inv.invoice_pdf_s3_url`
 *     `?? override.hosted_invoice_url ?? inv.hosted_invoice_url`
 *     `?? override.invoice_pdf ?? inv.invoice_pdf`
 *     `?? ''`
 */
export function resolveInvoiceDocumentUrl(
  inv: MaybeInvoiceDocumentUrlFields,
  override?: MaybeInvoiceDocumentUrlFields,
): string {
  const pick = (key: keyof InvoiceDocumentUrlFields): string | undefined => {
    const fromOverride = (override as Record<string, unknown> | null | undefined)?.[key];
    if (typeof fromOverride === 'string' && fromOverride.trim() !== '') {
      return fromOverride.trim();
    }
    const fromInv = (inv as Record<string, unknown> | null | undefined)?.[key];
    if (typeof fromInv === 'string' && fromInv.trim() !== '') {
      return fromInv.trim();
    }
    return undefined;
  };

  return pick('invoice_pdf_s3_url') ?? pick('hosted_invoice_url') ?? pick('invoice_pdf') ?? '';
}
